import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/useAppStore';
import { getInitials } from '../../utils/helpers';
import {
  Search, Bell, ChevronDown, Menu, X, Settings, LogOut, User,
  PanelLeftClose, PanelLeftOpen
} from 'lucide-react';

interface TopBarProps {
  onToggleSidebar: () => void;
}

export default function TopBar({ onToggleSidebar }: TopBarProps) {
  const { currentUser, unreadCount, notifications, globalSearch, setGlobalSearch, markAllRead, setSearchOpen, searchOpen } = useAppStore();
  const navigate = useNavigate();
  const [showNotifs, setShowNotifs] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const { sidebarCollapsed, toggleSidebarCollapse } = useAppStore();

  return (
    <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-40">
      {/* Left */}
      <div className="flex items-center gap-3">
        <button onClick={onToggleSidebar} className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <Menu className="w-5 h-5 text-slate-600" />
        </button>
        <button onClick={toggleSidebarCollapse} className="hidden lg:flex p-2 rounded-lg hover:bg-slate-100 transition-colors" title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          {sidebarCollapsed ? <PanelLeftOpen className="w-4 h-4 text-slate-500" /> : <PanelLeftClose className="w-4 h-4 text-slate-500" />}
        </button>

        {/* Search */}
        <div className="relative hidden md:block">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search vessels, inspections, reports..."
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            onFocus={() => setSearchOpen(true)}
            onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
            className="w-80 lg:w-96 pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-all"
          />
          <kbd className="hidden lg:inline-flex absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">⌘K</kbd>
        </div>

        {searchOpen && (
          <div className="absolute top-16 left-0 right-0 bg-white border-b border-slate-200 shadow-lg p-4 hidden md:block">
            <div className="max-w-3xl mx-auto">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">Quick Actions</p>
              <div className="grid grid-cols-3 gap-2">
                {['New Inspection', 'View Fleet', 'Generate Report', 'Add Vessel', 'Review CAPAs', 'View Analytics'].map(action => (
                  <button key={action} className="text-left px-3 py-2 rounded-lg hover:bg-slate-50 text-sm text-slate-700 transition-colors">
                    {action}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right */}
      <div className="flex items-center gap-2">
        {/* Mobile search */}
        <button className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <Search className="w-5 h-5 text-slate-500" />
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => { setShowNotifs(!showNotifs); setShowProfile(false); }}
            className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <Bell className="w-5 h-5 text-slate-500" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>

          {showNotifs && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowNotifs(false)} />
              <div className="absolute right-0 top-12 w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-800">Notifications</h3>
                  <button onClick={markAllRead} className="text-xs text-sky-600 hover:text-sky-700 font-medium">
                    Mark all read
                  </button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.slice(0, 8).map(n => (
                    <div key={n.id} className={`px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${!n.isRead ? 'bg-sky-50/50' : ''}`}>
                      <div className="flex gap-3">
                        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                          n.type === 'alert' ? 'bg-red-500' :
                          n.type === 'warning' ? 'bg-amber-500' :
                          n.type === 'success' ? 'bg-emerald-500' : 'bg-sky-500'
                        }`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{n.title}</p>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                          <p className="text-[10px] text-slate-400 mt-1">{new Date(n.timestamp).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-3 border-t border-slate-100 text-center">
                  <button className="text-sm text-sky-600 hover:text-sky-700 font-medium">View all notifications</button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Profile */}
        <div className="relative">
          <button
            onClick={() => { setShowProfile(!showProfile); setShowNotifs(false); }}
            className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-slate-100 transition-colors"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
              {currentUser ? getInitials(currentUser.name) : 'U'}
            </div>
            <div className="hidden lg:block text-left">
              <p className="text-sm font-medium text-slate-700 leading-tight">{currentUser?.name}</p>
              <p className="text-[11px] text-slate-400 capitalize">{currentUser?.role?.replace('_', ' ')}</p>
            </div>
            <ChevronDown className="hidden lg:block w-4 h-4 text-slate-400" />
          </button>

          {showProfile && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowProfile(false)} />
              <div className="absolute right-0 top-12 w-56 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 py-1">
                <div className="px-4 py-3 border-b border-slate-100">
                  <p className="font-medium text-slate-800">{currentUser?.name}</p>
                  <p className="text-xs text-slate-500">{currentUser?.email}</p>
                </div>
                <button className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors">
                  <User className="w-4 h-4 text-slate-400" /> My Profile
                </button>
                <button className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3 transition-colors">
                  <Settings className="w-4 h-4 text-slate-400" /> Settings
                </button>
                <hr className="my-1 border-slate-100" />
                <button
                  onClick={() => { useAppStore.getState().logout(); setShowProfile(false); navigate('/'); }}
                  className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                >
                  <LogOut className="w-4 h-4" /> Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
